import { useState, useRef, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../hooks/useChat';
import { useTypewriter } from '../../hooks/useTypewriter';

export function Hero() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { messages, loading, rateLimited, send } = useChat(lang);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const greetingText = t('chat.greeting');
  const { displayed, isTyping } = useTypewriter({ text: greetingText });
  const hasMessages = messages.length > 0;

  useEffect(() => {
    if (messages.length === 0) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    send(input.trim());
    setInput('');
  };

  return (
    <section id="hero" className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-3xl text-center">
        <p className="font-mono text-sm text-accent">{t('hero.greeting')}</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight md:text-6xl">
          Damián Jesús González
        </h1>
        <p className="mt-2 font-mono text-sm text-text-muted italic">
          {i18n.language === 'en'
            ? <>(Call me <span className="text-accent not-italic">Jesus</span> — everyone does)</>
            : <>(Decime <span className="text-accent not-italic">Jesús</span> — todos lo hacen)</>}
        </p>
        <p className="mt-4 font-mono text-lg text-text-muted">
          {t('hero.subtitle')}
        </p>
        <p className="mx-auto mt-8 max-w-xl text-text-muted leading-relaxed">
          {t('hero.intro')}
        </p>

        {/* Inline AI Chat */}
        <div className="mx-auto mt-10 max-w-lg">
          <div className="rounded border border-border/50 bg-bg-surface/50 backdrop-blur-sm">
            <div className="max-h-48 overflow-y-auto p-3 space-y-2 text-left">
              <div className="text-sm text-text-primary">
                <span className="font-mono text-xs text-accent">Dante &gt;</span>{' '}
                {hasMessages ? (
                  greetingText
                ) : (
                  <>{displayed}<span className={`inline-block w-[2px] h-[14px] align-middle bg-accent ml-[1px] ${isTyping ? 'animate-pulse' : ''}`} /></>
                )}
              </div>

              {messages.map((msg, idx) => (
                <div key={idx} className="text-sm">
                  <span className={`font-mono text-xs ${msg.role === 'assistant' ? 'text-accent' : 'text-text-muted'}`}>
                    {msg.role === 'assistant' ? 'Dante >' : (lang === 'es' ? 'Vos >' : 'You >')}
                  </span>{' '}
                  <span className={msg.role === 'assistant' ? 'text-text-primary' : 'text-text-muted'}>
                    {msg.content}
                  </span>
                </div>
              ))}

              {loading && (
                <div className="text-sm">
                  <span className="font-mono text-xs text-accent">Dante &gt;</span>{' '}
                  <span className="animate-pulse text-text-muted">...</span>
                </div>
              )}

              {rateLimited && (
                <div className="text-sm text-red-400">{t('chat.limitReached')}</div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="flex border-t border-border/50">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.placeholder')}
                disabled={rateLimited || loading}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={rateLimited || loading || !input.trim()}
                className="border-l border-border/50 px-4 font-mono text-xs text-accent transition-colors hover:bg-accent hover:text-bg-primary disabled:opacity-50"
              >
                {t('chat.send')}
              </button>
            </form>
          </div>
        </div>

        <a
          href="#experience"
          className="mt-8 inline-block border border-accent px-6 py-3 font-mono text-sm text-accent transition-colors hover:bg-accent hover:text-bg-primary"
        >
          {t('hero.cta')}
        </a>
      </div>
    </section>
  );
}
